/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable class-methods-use-this */
import { TransactionCharge } from "@models/TransactionCharge";
import { PaymentResponse } from "@models/PaymentResponse";
import { UserTransaction } from "@models/UserTransaction";
import { TransactionReportRecord } from "@models/TransactionReportRecord";
import { PaymentIntentRequest } from "@models/PaymentIntentRequest";
// eslint-disable-next-line object-curly-newline
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  getFirestore,
  doc,
  query,
  where,
  collection,
  getDocs,
  getDoc,
  Unsubscribe,
  onSnapshot,
  deleteDoc,
} from "firebase/firestore";
import Config from "@/config/Config";
import { AdminPaymentIntentRequest } from "@models/AdminPaymentIntentRequest";

class UserTransactionDataService {
  userCollectionName = "users";

  userTransactionName = "transactions";

  warmup(): void {
    const functions = getFunctions();
    const completeTransactionDirect = httpsCallable(functions, Config.functions.CompleteTransactionDirect);
    completeTransactionDirect({
      warmup: true
    });

    const calcFeesService = httpsCallable(functions, Config.functions.CalculateServiceFee);
    calcFeesService({
      warmup: true
    });

    const upsertPaymentIntent = httpsCallable(functions, Config.functions.UpsertPaymentIntent);
    upsertPaymentIntent({
      warmup: true
    });

    const adminCompleteTransactionDirectFunc = httpsCallable(functions, Config.functions.AdminCompleteTransactionDirect);
    adminCompleteTransactionDirectFunc({
      warmup: true
    });
  }  
  
  async adminCompleteTransactionDirect(request: AdminPaymentIntentRequest): Promise<PaymentResponse> {
    const functions = getFunctions();
    const adminCompleteTransactionDirectFunc = httpsCallable(functions, Config.functions.AdminCompleteTransactionDirect);
    const result = (await adminCompleteTransactionDirectFunc(
      request
    )) as any;

    if (result.data?.error) {
      throw result;
    }

    return result.data;
  }

  async completeTransactionDirect(transactionId: string): Promise<string | null> {
    const functions = getFunctions();
    const completeTransactionDirect = httpsCallable(functions, Config.functions.CompleteTransactionDirect);
    const result = (await completeTransactionDirect({
      transactionId,
    })) as any;

    if (result.data?.error) {
      throw result;
    }

    return null;
  }

  async generateTransactionReport(
    eventId: string
  ): Promise<TransactionReportRecord[]> {
    const functions = getFunctions();
    const calcFeesService = httpsCallable(functions, Config.functions.GenerateTransactionReport);
    const result = (await calcFeesService({
      eventId
    })) as any;

    if (result.data.error) {
      throw result;
    }

    return result.data as TransactionReportRecord[];
  }

  async calcServiceFee(
    eventId: string,
    amount: number,
    registrationCount: number
  ): Promise<TransactionCharge> {
    const functions = getFunctions();
    const calcFeesService = httpsCallable(functions, Config.functions.CalculateServiceFee);
    const result = (await calcFeesService({
      eventId,
      amount,
      registrationCount,
    })) as any;

    if (result.data.error) {
      throw result;
    }

    return result.data as TransactionCharge;
  }

  async startAdminTransactionDirect(request: PaymentIntentRequest): Promise<PaymentResponse> {
    const functions = getFunctions();
    const startAdminTransactionDirect = httpsCallable(functions, Config.functions.StartAdminTransactionDirect);
    const result = (await startAdminTransactionDirect(request)) as any;

    if (result.data.error) {
      throw result;
    }

    return result.data;
  }


  async paymentIntent(request: PaymentIntentRequest): Promise<PaymentResponse> {
    const functions = getFunctions();
    const upsertPaymentIntent = httpsCallable(functions, Config.functions.UpsertPaymentIntent);
    const result = (await upsertPaymentIntent(request)) as any;

    if (result.data.error) {
      throw result;
    }

    return result.data;
  }

  async getUserTransactions(userId: string, eventId: string): Promise<UserTransaction[]> {
    const db = getFirestore();
    const userDoc = doc(db, this.userCollectionName, userId);
    const userTransaction = collection(userDoc, this.userTransactionName);
    const q = query(userTransaction, where("eventId", "==", eventId));
    const snapshot = await getDocs(q);
    const results: UserTransaction[] = [];
    snapshot.forEach((item) => {
      results.push({ id: item.id, ...item.data() } as unknown as UserTransaction);
    });
    return results;
  }

  async getUserTransaction(userId: string, transactionId: string): Promise<UserTransaction> {
    const db = getFirestore();
    const userDoc = doc(db, this.userCollectionName, userId);
    const userTransactionRef = doc(collection(userDoc, this.userTransactionName), transactionId);

    const data = await getDoc(userTransactionRef);
    return {
      ...data.data(),
      id: data.id,
    } as unknown as UserTransaction;
  }

  async getUserTransactionSubscription(
    userId: string,
    transactionId: string,
    handleChange: (updatedLeaderboard: UserTransaction) => void
  ): Promise<Unsubscribe> {
    const db = getFirestore();
    const userDoc = doc(db, this.userCollectionName, userId);
    const userTransactionRef = doc(collection(userDoc, this.userTransactionName), transactionId);

    const unsubscribe = onSnapshot(userTransactionRef, (data) => {
      handleChange({
        ...data.data(),
        id: data.id,
      } as unknown as UserTransaction);
    });

    return unsubscribe;
  }

  // Delete user transactions
  async deleteUserTransactions(userId: string) {
    const db = getFirestore();
    const userDoc = doc(db, this.userCollectionName, userId);
    const userTransaction = collection(userDoc, this.userTransactionName);
    const q = query(userTransaction);
    const snapshot = await getDocs(q);
    snapshot.forEach(async (item) => {
      await deleteDoc(item.ref);
    });
  }
}

export default new UserTransactionDataService();
